import { createBrowserClient } from '@supabase/ssr'

// Single browser client instance - the ONLY browser client
let browserClient: ReturnType<typeof createBrowserClient> | null = null

// HMR-safe singleton using global
declare global {
  var __unified_supabase__: ReturnType<typeof createBrowserClient> | undefined
}

const COOKIE_NAME = 'sb-gjyl-auth-token'
// Support both Vite and Next.js environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  
  || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  
  || ''
const isProduction = import.meta.env.PROD
  

export function getUnifiedClient() {
  // Return existing singleton if available
  if (global.__unified_supabase__) {
    return global.__unified_supabase__
  }

  if (browserClient) {
    return browserClient
  }

  // Create the ONE browser client with proper PKCE configuration
  browserClient = createBrowserClient(
    supabaseUrl,
    supabaseAnonKey,
    {
      cookies: {
        // CRITICAL: These cookie handlers are required for PKCE OAuth flow
        get(name: string) {
          if (typeof window === 'undefined') return ''
          const cookies = document.cookie.split('; ')
          const cookie = cookies.find(c => c.startsWith(`${name}=`))
          if (!cookie) return ''

          try {
            return decodeURIComponent(cookie.split('=')[1])
          } catch {
            // Handle malformed cookies gracefully
            return ''
          }
        },
        set(name: string, value: string, options?: any) {
          if (typeof window === 'undefined') return

          let cookieString = `${name}=${encodeURIComponent(value)}`

          // Set proper defaults for PKCE cookies - don't set custom domain
          const finalOptions = {
            path: '/',
            sameSite: 'lax',
            secure: isProduction,
            ...options
          }

          if (finalOptions.maxAge) {
            cookieString += `; Max-Age=${finalOptions.maxAge}`
          }
          if (finalOptions.expires) {
            cookieString += `; Expires=${finalOptions.expires.toUTCString()}`
          }
          if (finalOptions.domain) {
            cookieString += `; Domain=${finalOptions.domain}`
          }
          cookieString += `; Path=${finalOptions.path}`
          cookieString += `; SameSite=${finalOptions.sameSite}`

          if (finalOptions.secure) {
            cookieString += `; Secure`
          }
          if (finalOptions.httpOnly) {
            // Can't set httpOnly from JavaScript, but include for completeness
            console.warn('Cannot set httpOnly cookie from browser JavaScript')
          }

          document.cookie = cookieString
        },
        remove(name: string, options?: any) {
          if (typeof window === 'undefined') return

          // Ensure we clear the cookie properly
          const finalOptions = {
            path: '/',
            ...options
          }

          let cookieString = `${name}=; Max-Age=0; Path=${finalOptions.path}`

          if (finalOptions.domain) {
            cookieString += `; Domain=${finalOptions.domain}`
          }

          document.cookie = cookieString

          // Also try to clear with expires in the past as backup
          const expired = new Date(0).toUTCString()
          let expiredString = `${name}=; Expires=${expired}; Path=${finalOptions.path}`
          if (finalOptions.domain) {
            expiredString += `; Domain=${finalOptions.domain}`
          }
          document.cookie = expiredString
        }
      },
      auth: {
        storageKey: COOKIE_NAME,
        detectSessionInUrl: true, // CRITICAL: Must be true for OAuth to complete properly
        flowType: 'pkce',
        persistSession: true,
        autoRefreshToken: true
      }
    }
  )

  // Store in global for HMR
  global.__unified_supabase__ = browserClient

  return browserClient
}

// OAuth helper functions using the unified client
function generateRandomState(length = 32) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : null as any
  if (cryptoObj?.getRandomValues) {
    const arr = new Uint32Array(length)
    cryptoObj.getRandomValues(arr)
    for (let i = 0; i < length; i++) {
      result += charset[arr[i] % charset.length]
    }
    return result
  }
  for (let i = 0; i < length; i++) {
    result += charset[Math.floor(Math.random() * charset.length)]
  }
  return result
}

export async function signInWithGoogle(intent: 'signin' | 'signup' = 'signin', next?: string) {
  const client = getUnifiedClient()

  const getBaseUrl = () => {
    if (import.meta.env.VITE_APP_URL) return import.meta.env.VITE_APP_URL
    if (typeof window !== 'undefined') return window.location.origin
    return 'http://localhost:3000'
  }

  // Don't clean up PKCE verifiers here - let Supabase manage them internally
  // Only clean up after successful auth or on error

  // Generate CSRF state and persist in a cookie for server callback verification
  const xstate = generateRandomState(32)
  try {
    // Store a short-lived state cookie readable by the server callback
    document.cookie = `gjyl-oauth-xstate=${encodeURIComponent(xstate)}; Max-Age=600; Path=/; SameSite=Lax${isProduction ? '; Secure' : ''}`
  } catch {}

  const params = new URLSearchParams({ intent })
  params.set('xstate', xstate)
  if (next && next.startsWith('/')) params.set('next', next)
  const redirectUrl = `${getBaseUrl()}/auth/callback?${params.toString()}`
  console.log('[OAuth] Starting Google sign-in with redirectTo:', redirectUrl)

  const { data, error } = await client.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectUrl,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      },
      skipBrowserRedirect: false,
    },
  })

  if (error) {
    console.error('[OAuth] Google sign-in error:', error)
  }

  return { data, error }
}

// Clean up PKCE verifiers to prevent stale state
export function cleanupPKCEVerifiers() {
  if (typeof window === 'undefined') return

  // Remove any PKCE-related cookies
  const cookies = document.cookie.split('; ')
  cookies.forEach(cookie => {
    const [name] = cookie.split('=')
    if (name.includes('code-verifier') || name.includes('auth-code-verifier')) {
      document.cookie = `${name}=; Max-Age=0; Path=/`
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${window.location.hostname}`
    }
  })

  // Clear any PKCE-related localStorage
  if (typeof localStorage !== 'undefined') {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.includes('code-verifier') || key.includes('pkce')) {
        localStorage.removeItem(key)
      }
    })
  }
}

// Get session with retries for after OAuth callback
export async function getSessionWithRetry(maxAttempts = 3, delayMs = 1000) {
  const client = getUnifiedClient()

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const { data: { session }, error } = await client.auth.getSession()

    if (session) {
      console.log(`[Auth] Session found on attempt ${attempt}`)
      return { session, error: null }
    }

    if (error) {
      console.error(`[Auth] Error getting session on attempt ${attempt}:`, error)
      return { session: null, error }
    }

    if (attempt < maxAttempts) {
      console.log(`[Auth] No session yet, waiting ${delayMs}ms before retry...`)
      await new Promise(resolve => setTimeout(resolve, delayMs))
    }
  }

  console.error('[Auth] No session found after all attempts')
  return { session: null, error: null }
}

// Check if user has an active session
export async function hasActiveSession(): Promise<boolean> {
  const client = getUnifiedClient()
  const { data: { session } } = await client.auth.getSession()
  return !!session
}

// Sign out and clean up
export async function signOut() {
  const client = getUnifiedClient()

  // Clean up PKCE verifiers
  cleanupPKCEVerifiers()

  // Sign out from Supabase
  const { error } = await client.auth.signOut()

  if (error) {
    console.error('[Auth] Sign out error:', error)
  }

  // Clear any app-specific cookies
  if (typeof document !== 'undefined') {
    document.cookie = 'gjyl-auth-session=; Max-Age=0; Path=/'
    document.cookie = 'gjyl-user-id=; Max-Age=0; Path=/'
    document.cookie = 'gjyl-oauth-pending=; Max-Age=0; Path=/'
  }

  return { error }
}

// Export the singleton for compatibility
export const unifiedSupabase = getUnifiedClient()
